<template>
  <div v-show="isArtWorkModalOpen" class="modal-popup modal-artwork">
    <div class="modal-btn-wrap view">
      <a
          v-if="location === 'favoriteArtwork' && activeArtWork"
          :href="link"
          target="_blank"
          rel="noopener noreferrer"
          class="btn"
          @click="emit('update:isArtWorkModalOpen', false)"
      >
        <span>아티스트 프로필 가기</span>
        <img src="@/assets/images/icon/ico_right.svg" alt="">
      </a>

      <HeartButton v-if="artWorks && artWorks.length && activeArtWork" v-model:isLike="activeArtWork.isLike" :uuid="activeArtWork.uuid"
                   :likeType="activeArtWork.likeType" :artistId="artistInfo.id" heartLocation="buttons"/>
      <ShareButton v-if="artWorks && artWorks.length && activeArtWork" :artist-user-account-id="activeArtWork.artistUserAccountId"
                   :uuid="activeArtWork.uuid"
                   location="button"/>
      <button type="button" class="close-modal close-artwork" aria-label="닫기" @click="historyBack"><img
          src="@/assets/images/icon/ico_close_b.svg" alt=""></button>
    </div>

    <div class="modal-con">
      <div class="swiper-container slider">
        <div class="swiper-wrapper">
          <div class="swiper-slide" v-for="artWork in artWorks" :key="artWork.uuid" :data-id="artWork.uuid">
            <div class="inner">
              <div class="img zoom-area swiper-zoom-container" @click="toggleArtWorkEnlarge" >
                <img :class="enlargedClass" :src="artWork.imageUrl"
                     :alt="artWork.title || artWork.titleEn" loading="lazy"/>
              </div>
              <div class="txt-box">
                <p style="white-space: pre-line;">{{ formatArtWorkText(artWork) }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="swiper-control">
          <div class="swiper-button-prev" @click="handlePrevClick"></div>
          <div class="swiper-button-next" @click="handleNextClick"></div>
        </div>
      </div>

      <div class="swiper-container slider-thumbnail">
        <div class="swiper-wrapper">
          <div class="swiper-slide" v-for="(artWork, index) in artWorks" :key="artWork.uuid" @click="handleThumbnailClick(index, artWork)">
            <img :src="artWork.imageUrl" :alt="artWork.title || artWork.titleEn" loading="lazy"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {defineEmits, defineProps, nextTick, onMounted, onUnmounted, ref, toRefs, watch, computed} from 'vue';
import {useRoute, useRouter} from "vue-router";
import Swiper from "swiper";
import HeartButton from "@/components/ui/HeartButton.vue";
import ShareButton from "@/components/ui/ShareButton.vue";
import { useModalAnalytics, MODAL_TYPES } from "@/composables/useModalAnalytics";
import { useAnalytics } from '@/composables/useAnalytics';
import { apiClient } from '@/services/auth-header';

const props = defineProps({
  isArtWorkModalOpen: {type: Boolean, default: false},
  artWorks: {type: Array, default: () => []},
  slideIndex: {type: Number, default: -1},
  location: {type: String, default: 'artistDetail'},
  totalPages: {type: Number, required: true},
  currentPage: {type: Number, required: true},
  fetchItems: {type: Function, required: true},
  artist: {type: Object, default: null},
});
const {artWorks, isArtWorkModalOpen, slideIndex, location, artist} = toRefs(props);

watch(() => props.isArtWorkModalOpen, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    // 모달 열림
    const currentArtwork = artWorks.value?.[slideIndex.value];
    modalId = trackModalOpen(MODAL_TYPES.ARTWORK_DETAIL, {
      contentId: currentArtwork?.uuid,
      contentType: 'artwork',
      artworkTitle: currentArtwork?.title,
      artistId: artistInfo.value?.id,  // artist prop에서 일관되게 가져오기
      location: props.location,
      totalCount: artWorks.value?.length || 0,
      initialIndex: slideIndex.value
    }, {
      triggerSource: props.location === 'favoriteArtwork' ? 'favorite' : 'gallery'
    });
    
    // 백엔드 조회수 증가 (첫 작품은 즉시, 디바운스 없이)
    trackArtworkViewCount(currentArtwork);
    
    // Analytics: 작품 상세 조회 추적
    trackCurrentArtworkView(currentArtwork, 'modal_open');
    
    openArtWorkModal(slideIndex.value);
    activeIndex.value = slideIndex.value;
  } else if (!newVal && oldVal && modalId) {
    // 모달 닫힘 - closeArtWorkModal에서 처리
  }
});

const route = useRoute();
const router = useRouter();
const emit = defineEmits(["update:isArtWorkModalOpen"]);

const link = computed(() => {
  return router.resolve({
    name: 'ArtistDetail',
    params: { userAccountId: activeArtWork.value.artistUserAccountId }
  }).href;
});

const isArtWorkEnlarged = ref(false);
const artWorkMainSwiper = ref(null);
const artWorkThumbnailSwiper = ref(null);

const activeIndex = ref(0); // 현재 슬라이드 인덱스 추적

// Analytics
const { trackArtworkView } = useAnalytics();
const { trackModalOpen, trackModalClose, trackModalInteraction } = useModalAnalytics();
let modalId = null;

// View tracking for backend
const viewedArtworks = new Set(); // 세션 내 중복 방지
let viewTrackTimeout = null;

// 개발 환경 체크 (컴파일 타임 상수로 최적화)
const isDev = process.env.NODE_ENV === 'development';

// 아티스트 정보 캐시 (성능 최적화 + 반응성 보장)
const artistInfo = computed(() => ({
  id: artist.value?.userAccountId || artist.value?.uuid,
  name: artist.value?.nameKo || artist.value?.nameEn || artist.value?.name || 'Unknown Artist'
}));

// 백엔드 조회수 증가 함수
const trackArtworkViewCount = async (artwork) => {
  if (!artwork || !artistInfo.value.id || viewedArtworks.has(artwork.uuid)) {
    return;
  }
  
  try {
    await apiClient.post(
      `/v1/artist/${artistInfo.value.id}/artworks/${artwork.uuid}/view`
    );
    viewedArtworks.add(artwork.uuid);
    
    if (isDev) {
      console.log('✅ 조회수 증가:', artwork.title || artwork.titleEn);
    }
  } catch (error) {
    if (isDev) {
      console.warn('⚠️ 조회수 증가 실패:', error);
    }
  }
};

// 디바운스된 조회수 증가 함수
const debouncedTrackView = (artwork) => {
  clearTimeout(viewTrackTimeout);
  viewTrackTimeout = setTimeout(() => {
    trackArtworkViewCount(artwork);
  }, 500); // 500ms 디바운스
};

// 작품 analytics 추적 함수 (중복 제거 + 성능 최적화)
const trackCurrentArtworkView = (artwork, source = 'modal') => {
  if (!artwork || !artist.value) return;
  
  // 개발 환경에서만 디버깅 로그
  if (isDev) {
    console.log(`🔍 [${source}] Artwork Data:`, artwork);
    console.log(`🔍 [${source}] Artist Data:`, artist.value);
  }
  
  try {
    trackArtworkView({
      id: artwork.uuid,
      title: artwork.title || artwork.titleEn || 'Untitled',
      artistId: artistInfo.value.id,
      artistName: artistInfo.value.name,
      category: artwork.genre || artwork.category || 'Unknown',
      style: artwork.themeStyle || artwork.style || 'Unknown',
      medium: artwork.material || artwork.medium || 'Unknown',
      price: artwork.price || 0
    });
  } catch (error) {
    if (isDev) {
      console.warn(`Analytics tracking failed for ${source}:`, error);
    }
  }
};

// 네비게이션 원인 추적 (중복 이벤트 방지)
let navigationSource = null;

const activeArtWork = computed(() => {
  if (!artWorks.value || !artWorks.value.length) {
    return null;
  }
  return artWorks.value[activeIndex.value];
});

const enlargedClass = computed(() => ({
  enlarged: isArtWorkEnlarged.value
}));

const toggleArtWorkEnlarge = () => {
  isArtWorkEnlarged.value = !isArtWorkEnlarged.value;
  
  // 이미지 확대/축소 상호작용 추적
  if (modalId) {
    const currentArtwork = artWorks.value?.[activeIndex.value];
    trackModalInteraction(MODAL_TYPES.ARTWORK_DETAIL, 'click', {
      elementType: 'image_zoom',
      elementId: 'artwork_image',
      value: isArtWorkEnlarged.value ? 'zoom_in' : 'zoom_out',
      artworkId: currentArtwork?.uuid,
      currentIndex: activeIndex.value
    });
  }
};

const initializeSwiper = async () => {
  await nextTick();

  artWorkThumbnailSwiper.value = new Swiper('.slider-thumbnail', {
    slidesPerView: 'auto',
    freeMode: true,
    grabCursor: true,
    spaceBetween: 20,
    centeredSlides: true,
    breakpoints: {
      769: {spaceBetween: 10},
      1025: {spaceBetween: 15},
    },
  });

  artWorkMainSwiper.value = new Swiper('.slider', {
    slidesPerView: 'auto',
    grabCursor: true,
    zoom: true,
    mousewheel: true,
    keyboard: {
      enabled: true,
    },
    navigation: {
      prevEl: '.swiper-button-prev',
      nextEl: '.swiper-button-next',
    },
    thumbs: {
      swiper: artWorkThumbnailSwiper.value
    },
    on: {
      slideChange: () => {
        const previousIndex = activeIndex.value;
        activeIndex.value = artWorkMainSwiper.value.activeIndex;
        artWorkThumbnailSwiper.value.slideTo(activeIndex.value, 200, true);

        // 통합된 네비게이션 Analytics 추적 (중복 방지)
        if (modalId && previousIndex !== activeIndex.value) {
          const currentArtwork = artWorks.value?.[activeIndex.value];
          const direction = activeIndex.value > previousIndex ? 'next' : 'prev';
          
          // 네비게이션 원인 결정 (우선순위: 저장된 원인 > 추론)
          const method = navigationSource || (
            Math.abs(activeIndex.value - previousIndex) === 1 ? 'swipe' : 'jump'
          );
          
          // 백엔드 조회수 증가 (디바운스 적용)
          debouncedTrackView(currentArtwork);
          
          // Analytics: 새로운 작품 조회 추적
          trackCurrentArtworkView(currentArtwork, 'slide_change');
          
          trackModalInteraction(MODAL_TYPES.ARTWORK_DETAIL, 'navigation', {
            elementType: 'slide_change',
            direction: direction,
            fromIndex: previousIndex,
            toIndex: activeIndex.value,
            artworkId: currentArtwork?.uuid,
            artworkTitle: currentArtwork?.title,
            navigationMethod: method
          });
          
          // 원인 초기화
          navigationSource = null;
        }

        if (activeIndex.value >= artWorks.value.length - 2) {
          if (props.currentPage + 1 < props.totalPages) {
            props.fetchItems(props.currentPage + 1, true);
          }
        }
      }
    }
  });

  artWorkMainSwiper.value?.update();
  artWorkThumbnailSwiper.value?.update();
};

const openArtWorkModal = async (slideIndex) => {
  await nextTick();
  window.history.pushState({modalOpen: true}, "", window.location.href);
  await nextTick(() => {
    // 먼저 메인 슬라이드와 썸네일 슬라이드 업데이트
    artWorkMainSwiper.value.update();
    artWorkThumbnailSwiper.value.update();
    if (artWorkMainSwiper.value) {
      if (slideIndex !== -1) {
        artWorkMainSwiper.value.slideTo(slideIndex, 0, false); // 해당 슬라이드로 즉시 이동
        // artWorkThumbnailSwiper.value.slideTo(slideIndex, 0, false); // 해당 슬라이드로 즉시 이동
        // artWorkMainSwiper.value.slideTo(slideIndex, 500, true) // 애니메이션
        // artWorkThumbnailSwiper.value.slideTo(slideIndex, 500, true) // 애니메이션
      } else {
        console.warn(`Slide with id ${slideIndex} not found`);
      }
    }
  });
};

const historyBack = () => {
  window.history.back();
}

const onArtWorksModalPopState = (event) => {
  closeArtWorkModal();
};

const closeArtWorkModal = () => {
  if (modalId) {
    const currentArtwork = artWorks.value?.[activeIndex.value];
    trackModalClose(MODAL_TYPES.ARTWORK_DETAIL, 'user_action', {
      success: true,
      finalIndex: activeIndex.value,
      viewedArtworks: activeIndex.value + 1,
      artworkId: currentArtwork?.uuid
    });
    modalId = null;
  }
  
  emit("update:isArtWorkModalOpen", false);
  if (route.query?.artwork) {
    router.replace({query: {...route.query, artwork: undefined}});
  }
};

// 네비게이션 버튼 클릭 핸들러 (원인만 저장, 이벤트는 slideChange에서)
const handlePrevClick = () => {
  if (modalId && artWorkMainSwiper.value) {
    navigationSource = 'button_click';
  }
};

const handleNextClick = () => {
  if (modalId && artWorkMainSwiper.value) {
    navigationSource = 'button_click';
  }
};

// 썸네일 클릭 핸들러 (원인만 저장, 이벤트는 slideChange에서)
const handleThumbnailClick = (index, artWork) => {
  if (modalId && index !== activeIndex.value) {
    navigationSource = 'thumbnail_click';
  }
};

const formatArtWorkText = (artWork) => {
  let formattedText = [artWork.title, artWork.titleEn].filter(Boolean).join(' | ');

  // 캡션 노출 순서: 연도 > 재료 > 크기 (제목/영문제목 다음)
  const additionalInfo = [artWork.year, artWork.material, artWork.size].filter(Boolean).join(' | ');

  if (additionalInfo) {
    formattedText += `\n${additionalInfo}`;
  }

  return formattedText;
};

onMounted(async () => {
  window.addEventListener("popstate", onArtWorksModalPopState);
  await initializeSwiper();
})

onUnmounted(() => {
  window.removeEventListener("popstate", onArtWorksModalPopState);
  
  // 조회수 추적 정리
  clearTimeout(viewTrackTimeout);
  viewedArtworks.clear();
  
  if (artWorkMainSwiper.value) {
    artWorkMainSwiper.value.destroy();
    artWorkMainSwiper.value = null;
  }
  if (artWorkThumbnailSwiper.value) {
    artWorkThumbnailSwiper.value.destroy();
    artWorkThumbnailSwiper.value = null;
  }
});

</script>


<style scoped>
.modal-artwork .modal-con .slider .swiper-slide .inner .img img {
  filter: drop-shadow(16px 16px 8px rgba(0, 0, 0, 0.5)) drop-shadow(-2px -2px 8px rgba(0, 0, 0, 0.5));
}

.modal-popup.modal-artwork {
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently supported by Chrome, Opera and Firefox */
}

.modal-artwork .modal-con {
  height: 100dvh;
  overflow: hidden; /* 스크롤 없애기 */
  display: flex;
  flex-direction: column;
  padding-bottom: 0; /* 필요 시 조정 */
}

.modal-con .slider {
  flex: 5;
}

.modal-con .slider-thumbnail {
  flex: 1;
}

.modal-artwork .modal-con .slider-thumbnail .swiper-slide {
  overflow: hidden;
  border-radius: 10px;
  cursor: pointer;
  width: auto;
  height: auto;
  aspect-ratio: 1 / 1;
  object-fit: cover;
}

/* swiper 테스트중
.modal-artwork .modal-con .slider-thumbnail .swiper-slide img{
  aspect-ratio: 1 / 1;
  object-fit: cover;
}

.enlarged {
  transform: scale(1.5);
  transition: transform 0.3s ease-in-out;
}

.swiper-slide .img img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.modal-artwork .modal-con .slider-thumbnail .swiper-slide img {
  aspect-ratio: 1 / 1;
}



.modal-artwork .modal-con {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-artwork .modal-con .slider {
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh
}

.modal-artwork .modal-con .slider .swiper-slide {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.modal-artwork .modal-con .slider .swiper-slide .inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.modal-artwork .modal-con .slider .swiper-slide .inner .img {
  width: 100%;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}



.modal-artwork .modal-con .slider .swiper-slide .inner .img img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.modal-artwork .modal-con .slider .swiper-slide .inner .txt-box {
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding-top: 20px;
}

*/

</style>